/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import { Button, FormControl, Select, MenuItem, CircularProgress } from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { BRAND_COLOR, ERROR_COLOR, GRAY3_COLOR } from 'lib/constants'
import { useVoice, normalizeChannelId, normalizeChannelName } from 'contexts/VoiceProvider'
import { useGlobal } from 'contexts/GlobalProvider'
import { addUserToChannel } from 'models/api/channels'
import type { Channel } from 'lib/types/channels'
import PTTButton from 'components/partials/PTTButton'

const StyledSelect = styled(Select)({
  background: GRAY3_COLOR,
  borderRadius: 10,
  minWidth: 220,
  paddingLeft: 8,
  paddingRight: 8,
  '.MuiSelect-select': { paddingTop: 10, paddingBottom: 10 },
})

export default function VoicePTT() {
  const { selectedOrgId, currentUserV5 } = useGlobal()

  const {
    isConnected,
    isKicked,
    joiningChannelId,
    currentChannelId,
    currentChannelName,
    availableChannels,
    setTxChannel,
    leaveChannel,
    pttStart,
    pttStop,
    zelloState,
    voiceReady,
    channelBusy,
    canPTT,
    reconnect,
    isChannelReady,
  } = useVoice()

  const switching = Boolean(joiningChannelId && joiningChannelId !== currentChannelId)
  const wsConnecting = zelloState.status === 'connecting'
  const channelReady = isChannelReady(currentChannelName)
  const connectionFailed = Boolean(
    zelloState.connectionFailed && !!currentChannelId && !channelReady,
  )
  // Show the connecting state until both the socket is up AND the channel
  // has emitted on_channel_status: online. start_stream before that comes
  // back as "channel is not ready" (Zello spec §158-160). Once the watchdog
  // gives up, switch to a terminal "Connection failed" state.
  const isConnecting =
    !connectionFailed && (wsConnecting || (isConnected && !!currentChannelId && !channelReady))
  const pttActive = zelloState.tx === 'sending'
  const lastError: string | undefined = zelloState.error

  const disabled =
    !voiceReady || !isConnected || isConnecting || switching || zelloState.status !== 'connected' || !currentChannelId

  const selectValue = currentChannelId ? String(currentChannelId) : ''
  const selectDisabled = !voiceReady || switching

  const handleChannelChange = async (value: string) => {
    if (!value) {
      leaveChannel()
      setTxChannel(null)
      return
    }

    const next = availableChannels.find((c: Channel) => normalizeChannelId(c) === value)
    if (!next) return

    const cname = normalizeChannelName(next)
    const orgId = selectedOrgId || localStorage.getItem('selected_org_id') || undefined

    if (cname && currentUserV5?.id && orgId) {
      try {
        await addUserToChannel(currentUserV5.id, cname, orgId)
      } catch {
        /* ignore */
      }
    }

    setTxChannel(next)
  }

  const disabledTooltip = (() => {
    if (wsConnecting) return 'Connecting…'
    if (isConnected && !!currentChannelId && !channelReady) return 'Channel coming online…'
    if (switching) return 'Switching channels…'
    if (!voiceReady) return 'Voice is not configured'
    if (!isConnected || zelloState.status !== 'connected') return 'Voice is not connected'
    if (!currentChannelId) return 'Select a channel first'
    if (channelBusy) return 'Channel busy'
    if (!canPTT) return 'Mic not ready'
    return undefined
  })()

  if (isKicked) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 24 }}>
        <span style={{ fontSize: 13, color: ERROR_COLOR }}>
          Another device logged in — voice disconnected.
        </span>
        <Button variant="outlined" size="small" onClick={reconnect} style={{ borderColor: BRAND_COLOR, color: BRAND_COLOR }}>
          Reconnect
        </Button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginRight: 24 }}>
      <FormControl disabled={selectDisabled}>
        <StyledSelect
          value={selectValue}
          onChange={e => handleChannelChange(String(e.target.value))}
          IconComponent={KeyboardArrowDownIcon}
          disableUnderline
          displayEmpty
          renderValue={(value: unknown) => {
            const v = String(value ?? '')
            if (!v) {
              return <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>No channel</span>
            }

            const ch = availableChannels.find((c: Channel) => normalizeChannelId(c) === v)
            const label = ch ? normalizeChannelName(ch) || 'No channel' : 'No channel'
            return <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>{label}</span>
          }}
          MenuProps={{
            getContentAnchorEl: null,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            PaperProps: { style: { borderRadius: 12 } },
          }}
        >
          <MenuItem value="" style={{ textAlign: 'center' }}>
            <em>No channel</em>
          </MenuItem>

          {availableChannels.map((c: Channel) => {
            const cid = normalizeChannelId(c)!
            const cname = normalizeChannelName(c) || '(unnamed)'
            const isJoining = String(joiningChannelId || '') === cid

            return (
              <MenuItem key={cid} value={cid} style={{ textAlign: 'center' }}>
                {isJoining ? <CircularProgress size={14} style={{ marginRight: 8 }} /> : null}
                {cname}
              </MenuItem>
            )
          })}
        </StyledSelect>
      </FormControl>

      <PTTButton
        disabled={disabled}
        connecting={isConnecting}
        sending={pttActive}
        connectionFailed={connectionFailed}
        onReconnect={reconnect}
        lastError={lastError}
        canPTT={canPTT}
        disabledTooltip={disabledTooltip}
        onStart={pttStart}
        onStop={pttStop}
      />
    </div>
  )
}
